<script lang="ts">
export default {
  name: 'Step7Review',
};
</script>

<script setup lang="ts">
import { computed, onBeforeMount, onBeforeUnmount, ref, watchEffect } from 'vue';
import { CircleCheck, RefreshCw, TriangleAlert } from '@lucide/vue';
import { useDataReadinessWizard, WIZARD_STEPS } from '@/composables/useDataReadinessWizard';
import { useToast } from '@/composables/useToast';
import ReviewSummaryTable, { type ReviewSummaryItem } from '@/components/v2/data_readiness/ReviewSummaryTable.vue';
import AppModal from '@/components/v2/AppModal.vue';
import AppButton from '@/components/v2/AppButton.vue';
import Alert from '@/components/v2/Alert.vue';

const wizard = useDataReadinessWizard();
const toast = useToast();

const loadFailed = ref(false);

// Re-fetch every source on entry so the summary reflects work done in earlier
// steps this session (Step 4's autosave nav guard runs before this mounts).
async function loadAll() {
  loadFailed.value = false;
  // allSettled so one failing source doesn't wipe the rest of the summary; the
  // per-row skeleton (via null issue count) surfaces which source is unknown.
  const results = await Promise.allSettled([
    wizard.refreshDocuments('Binder'),
    wizard.refreshDocuments('Policy'),
    wizard.refreshRuleViolatingPolicies(),
    wizard.refreshDemographics(),
    wizard.refreshPrograms(),
    wizard.refreshPoliciesWithoutProgram(),
  ]);
  if (results.every((r) => r.status === 'rejected')) {
    loadFailed.value = true;
  }
}

onBeforeMount(loadAll);

const isLoading = computed(
  () =>
    wizard.loadingBinders.value ||
    wizard.loadingPolicies.value ||
    wizard.loadingRules.value ||
    wizard.loadingDemographics.value ||
    wizard.loadingPrograms.value ||
    wizard.loadingPoliciesWithoutProgram.value,
);

// The five prior steps (this step is the last WIZARD_STEPS entry).
const stepRows = computed<ReviewSummaryItem[]>(() =>
  WIZARD_STEPS.slice(0, -1).map((step, i) => ({
    key: step.key,
    label: step.label,
    count: wizard.stepIssueCounts.value[i],
    caption: step.key === 'epic' ? 'Corrections sync overnight from Epic' : undefined,
  })),
);

const totalIssues = computed(() => stepRows.value.reduce((sum, r) => sum + (r.count ?? 0), 0));
const stepsWithIssues = computed(() => stepRows.value.filter((r) => (r.count ?? 0) > 0).length);
const countsKnown = computed(() => !isLoading.value && !loadFailed.value && stepRows.value.every((r) => r.count != null));
const ready = computed(() => countsKnown.value && totalIssues.value === 0);

watchEffect(() => {
  if (isLoading.value) {
    wizard.setFooterHint({ text: 'Checking readiness…', color: 'muted' });
  } else if (!countsKnown.value) {
    wizard.setFooterHint({ text: "Couldn't load readiness summary", color: 'amber' });
  } else if (totalIssues.value === 0) {
    wizard.setFooterHint({ text: 'All steps complete — ready to finish', color: 'green' });
  } else {
    const n = totalIssues.value;
    wizard.setFooterHint({
      text: `${n} issue${n === 1 ? '' : 's'} remain — you can still finish`,
      color: 'amber',
    });
  }
});

function goToStep(index: number) {
  wizard.setStep(index);
}

// -------------------------------------------------------------------------
// Finish confirmation: registered with the wizard so the footer's Finish
// button routes through here. Clean state exits immediately with a toast;
// outstanding (or unverifiable) issues require an explicit acknowledgment.
// -------------------------------------------------------------------------
const showFinishConfirm = ref(false);
let finishResolver: ((confirmed: boolean) => void) | null = null;

function beforeFinish(): boolean | Promise<boolean> {
  if (ready.value) {
    toast.success('Data readiness complete.');
    return true;
  }
  showFinishConfirm.value = true;
  return new Promise<boolean>((resolve) => {
    finishResolver = resolve;
  });
}

function confirmFinish() {
  showFinishConfirm.value = false;
  finishResolver?.(true);
  finishResolver = null;
}

function cancelFinish() {
  showFinishConfirm.value = false;
  finishResolver?.(false);
  finishResolver = null;
}

const unregisterBeforeFinish = wizard.registerBeforeFinish(beforeFinish);

onBeforeUnmount(() => {
  unregisterBeforeFinish();
  wizard.setFooterHint(null);
  // Don't leave the wrapper's Finish button awaiting an orphaned promise.
  finishResolver?.(false);
  finishResolver = null;
});
</script>

<template>
  <div class="p-6">
    <h2 class="mb-1 text-base font-semibold">Review & finalize</h2>
    <p class="mb-4 text-sm text-base-content/60">
      Here's a summary of outstanding issues across every step of this wizard. You can jump back to resolve anything before finishing, or finish now —
      issues that require an Epic sync will resolve automatically once corrected there.
    </p>

    <!-- Overall readiness banner -->
    <div
      class="mb-4 flex items-center gap-3 rounded-md border px-4 py-3"
      :class="{
        'border-[#BFE0D3] bg-[#E1F5EE]': ready,
        'border-[#EBD9B4] bg-[#FAEEDA]': countsKnown && !ready,
        'border-base-300 bg-base-200/50': !countsKnown,
      }"
      data-testid="readiness-banner">
      <template v-if="!countsKnown">
        <span class="text-sm text-base-content/60">
          {{ isLoading ? 'Checking readiness…' : 'Readiness could not be verified.' }}
        </span>
      </template>
      <template v-else-if="ready">
        <div class="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#0F6E56]/15">
          <CircleCheck
            :size="18"
            class="text-[#0F6E56]" />
        </div>
        <div class="min-w-0 flex-1">
          <div class="font-semibold text-[#0F6E56]">Ready</div>
          <div class="text-sm text-[#0F6E56]/80">No outstanding issues across all steps.</div>
        </div>
      </template>
      <template v-else>
        <div class="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#854F0B]/15">
          <TriangleAlert
            :size="18"
            class="text-[#854F0B]" />
        </div>
        <div class="min-w-0 flex-1">
          <div class="font-semibold text-[#854F0B]">Not ready — issues remain</div>
          <div class="text-sm text-[#854F0B]/80">
            {{ totalIssues }} issue{{ totalIssues === 1 ? '' : 's' }} across {{ stepsWithIssues }} step{{ stepsWithIssues === 1 ? '' : 's' }}. You can
            still finish and follow up later.
          </div>
        </div>
        <span class="shrink-0 text-3xl font-bold text-[#854F0B]">{{ totalIssues }}</span>
      </template>
    </div>

    <Alert
      v-if="loadFailed"
      type="error"
      :icon="TriangleAlert"
      class="mb-4">
      <div class="flex items-center gap-3">
        <span>Couldn't load the readiness summary.</span>
        <AppButton
          outline
          size="xs"
          @click="loadAll">
          <RefreshCw :size="14" />
          Retry
        </AppButton>
      </div>
    </Alert>

    <ReviewSummaryTable
      :items="stepRows"
      :loading="isLoading"
      @review="goToStep" />

    <AppModal
      title="Finish with outstanding issues?"
      :show="showFinishConfirm"
      action-button-label="Finish anyway"
      :handle-action="confirmFinish"
      :handle-cancel="cancelFinish">
      <template v-slot:content>
        <p v-if="countsKnown">
          {{ totalIssues }} issue{{ totalIssues === 1 ? '' : 's' }} remain{{ totalIssues === 1 ? 's' : '' }} across {{ stepsWithIssues }}
          step{{ stepsWithIssues === 1 ? '' : 's' }}. You can finish now and return later, or cancel to keep reviewing.
        </p>
        <p v-else>Readiness could not be verified. You can finish now, or cancel to keep reviewing.</p>
      </template>
    </AppModal>
  </div>
</template>
